import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useMemo, useState } from "react";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import { Link } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import styles from "@/components/subjectOverview/subjectOverviewAppointments.module.css";
import {
  getStatusLabel,
  getTranslatedStatusOptions,
} from "@/helper/statusOptions";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { replaceDate } from "@/helper/dateHandling";
import SubjectOverviewAppointmentCreateModal from "@/components/subjectOverview/subjectOverviewAppointmentCreateModal";
import { appointmentEntityAdapter } from "@/components/appointment/appointmentEntityAdapter";

interface Props {
  detailedSubject?: any;
  openWithSpecificSubjectIdUrl?: boolean;
}

const SubjectOverviewAppointments: FC<Props> = ({
  detailedSubject,
  openWithSpecificSubjectIdUrl = false,
}) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [refreshTable, setRefreshTable] = useState(false);
  const { t } = useTranslation();
  const statusFilterOptions = getTranslatedStatusOptions("appointment");

  const handleCreateAppointment = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleAppointmentCreated = () => {
    setIsCreateModalOpen(false);
  };

  useEffect(() => {
    const handler = () => {
      setRefreshTable((prev) => !prev);
    };
    window.addEventListener(
      "new_appointment_created_in_appointment_dashboard_detail_view",
      handler as any
    );
    return () =>
      window.removeEventListener(
        "new_appointment_created_in_appointment_dashboard_detail_view",
        handler as any
      );
  }, []);

  const customTopToolbarAction = () => {
    const subjectId = detailedSubject?.id;
    const linkTo =
      openWithSpecificSubjectIdUrl && subjectId
        ? `/appointmentList/${subjectId}`
        : "/appointmentList";
    return (
      <GenericButton startIcon={<OpenInNewIcon />}>
        <Link
          to={linkTo}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link_white}
        >
          {t("appointment_multi")}
        </Link>
      </GenericButton>
    );
  };
  const columns = useMemo<CrudColumnDef[]>(
    () => [
      {
        accessorKey: "id",
        header: t("id"),
        id: "id",
      },
      {
        header: t("datetime"),
        accessorKey: "datetime",
        id: "datetime",
        filterVariant: "date",
        sortingFn: "datetime",
        maxSize: 160,
        accessorFn: (row: any) => {
          return (
            <Box className={styles.boxContainer}>
              <Link
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link_purple}
                to={`/appointmentDashboard/${row.id}`}
              >
                {replaceDate(row.datetime, true)}
              </Link>
            </Box>
          );
        },
      },
      {
        header: t("name"),
        accessorKey: "examination_set",
        maxSize: 840,
        id: "examination_set__name",
        accessorFn: (row: any) => {
          if (!Array.isArray(row.examination_set)) {
            return "";
          }
          return row.examination_set
            .map((exam: any) => exam?.name)
            .filter(Boolean)
            .join(", ");
        },
      },
      {
        header: t("status"),
        accessorKey: "status",
        id: "status",
        filterVariant: "select",
        filterSelectOptions: statusFilterOptions,
        accessorFn: (row: any) => {
          const appointmentStatus = getStatusLabel(row?.status);
          return <span>{appointmentStatus}</span>;
        },
      },
      {
        header: t("executed_by"),
        accessorKey: "executed_by",
        id: "executed_by",
        customColumnFilterId: "executed_by_name",
        customSortingKey: "executed_by_name",
        enableForCreate: true,
        entity: "user",
        accessorFn: (row: any) => {
          if (row.executed_by && row.executed_by !== null) {
            return row.executed_by.name;
          } else return "-";
        },
      },
      {
        header: t("assisted_by"),
        accessorKey: "assisted_by",
        id: "assisted_by",
        customColumnFilterId: "assisted_by_name",
        customSortingKey: "assisted_by_name",
        enableForCreate: true,
        entity: "user",
        accessorFn: (row: any) => {
          if (row.assisted_by && row.assisted_by !== null) {
            return row.assisted_by.name;
          } else return "-";
        },
      },
      ...getRecordMetadataColumns(t, ["created_on", "created_by", "modified_on", "modified_by"]),
    ],
    []
  );
  return (
    <>
      <GenericCrudTable
        entity="appointment"
        entityAdapter={appointmentEntityAdapter}
        columns={columns}
        customTopToolBarAction={() => customTopToolbarAction()}
        customGlobalFilterPosition={"left"}
        customDensity="compact"
        customPageSize={5}
        showRowActions={false}
        searchId={detailedSubject?.id}
        expandedDepth={true}
        tableCollapseWidth={"90%"}
        customCreateHandler={handleCreateAppointment}
        refreshTable={refreshTable}
      />
      {isCreateModalOpen && (
        <SubjectOverviewAppointmentCreateModal
          open={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          detailedSubject={detailedSubject}
          onAppointmentCreated={handleAppointmentCreated}
        />
      )}
    </>
  );
};

export default SubjectOverviewAppointments;
